import { useEffect } from 'react';
import { PartnerDataType } from '../contexts/PartnerData';
import { isPartnerPersonalDomain } from '../backendSelection';

export const IFOOD_BROWSER_TITLE = 'i-food - the best food delivery service';
export const IFOOD_FAVICON_URL = '/Assets/images/logo_ifood.png';
export const NEUTRAL_FAVICON_DATA_URL = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%23F7F8F5'/%3E%3C/svg%3E";

type BrowserTabBrandingProps = {
  partnerData: PartnerDataType | null;
  frontendOrigin: string;
  frontendHostname: string;
  logoBaseUrl: string;
};

type BrowserTabBrandingState = {
  title: string;
  faviconUrl: string;
};

const setIconLink = (rel: string, href: string): void => {
  const selector = `link[rel="${rel}"]`;
  let linkElement = document.head.querySelector<HTMLLinkElement>(selector);

  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.rel = rel;
    document.head.appendChild(linkElement);
  }

  linkElement.href = href;

  if (href.startsWith('data:image/svg+xml')) {
    linkElement.type = 'image/svg+xml';
  } else if (href.toLowerCase().endsWith('.png')) {
    linkElement.type = 'image/png';
  } else {
    linkElement.removeAttribute('type');
  }
};

export const resolveBrowserTabBranding = (
  partnerData: PartnerDataType | null,
  frontendOrigin: string,
  frontendHostname: string,
  logoBaseUrl: string,
): BrowserTabBrandingState => {
  if (!isPartnerPersonalDomain(frontendOrigin, frontendHostname)) {
    return {
      title: IFOOD_BROWSER_TITLE,
      faviconUrl: IFOOD_FAVICON_URL,
    };
  }

  const partnerLogo = partnerData?.logotype?.trim();
  return {
    title: frontendHostname.toLowerCase(),
    faviconUrl: partnerLogo ? `${logoBaseUrl}${partnerLogo}` : NEUTRAL_FAVICON_DATA_URL,
  };
};

/**
 * BrowserTabBranding
 *
 * Keeps the browser tab title and favicon synchronized with the current
 * domain branding rule. It receives partner data from App.tsx, depends on the
 * shared backend-domain helper and logo asset base URL, and performs only DOM
 * side effects on document title and icon link tags.
 */
function BrowserTabBranding({
  partnerData,
  frontendOrigin,
  frontendHostname,
  logoBaseUrl,
}: BrowserTabBrandingProps) {
  useEffect(() => {
    const branding = resolveBrowserTabBranding(partnerData, frontendOrigin, frontendHostname, logoBaseUrl);
    document.title = branding.title;
    setIconLink('icon', branding.faviconUrl);
    setIconLink('apple-touch-icon', branding.faviconUrl);
  }, [frontendHostname, frontendOrigin, logoBaseUrl, partnerData]);

  return null;
}

export default BrowserTabBranding;
